import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getOrgasmStatus, getOrgasmRequests, createOrgasmRequest, respondOrgasmRequest, deleteOrgasmRequest, getOrgasmLogs, createOrgasmLog, deleteOrgasmLog, controlAllow, controlDeny, controlEdge, controlRelease, controlLogEdge, getControlHistory, deleteControlHistory, extractErrorMessage } from '../services/api';
import { FaPlus, FaCheck, FaTimes, FaCircle, FaTrash } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { getGuildMembers } from '../services/api';
import { useToast } from '../components/Toast';

function OrgasmControl({ user }) {
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [requests, setRequests] = useState([]);
  const [logs, setLogs] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('status');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [savingRequest, setSavingRequest] = useState(false);
  const [savingLog, setSavingLog] = useState(false);
  const [formErrorRequest, setFormErrorRequest] = useState(null);
  const [formErrorLog, setFormErrorLog] = useState(null);
  const [newRequest, setNewRequest] = useState({
    user_id: '',
    reason: ''
  });
  const [newAction, setNewAction] = useState({
    user_id: '',
    action: 'allow',
    condition: '',
    duration: '',
    reason: '',
    count: '',
    timeframe: '',
    release_type: 'allowed'
  });
  const [membersMap, setMembersMap] = useState({});

  useEffect(() => {
    fetchData();
    // Load guild members for name resolution
    getGuildMembers().then(data => {
      const map = {};
      for (const m of data) map[m.id] = m.displayName;
      setMembersMap(map);
    }).catch(() => {});
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statusData, requestsData, logsData, historyData] = await Promise.all([
        getOrgasmStatus(user.id).catch(err => {
          console.error('Status load failed:', err);
          return null;
        }),
        getOrgasmRequests({ status: 'all' }).catch(() => []),
        getOrgasmLogs().catch(() => ({ logs: [] })),
        getControlHistory().catch(() => ({ entries: [] }))
      ]);
      setStatus(statusData || { status: 'allowed' });
      setRequests(requestsData.requests || requestsData || []);
      setLogs(logsData.logs || logsData || []);
      setHistory(historyData.entries || historyData || []);
    } catch (error) {
      console.error('Error fetching orgasm control data:', error);
      toast.error(extractErrorMessage(error, 'Daten konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    if (savingRequest) return;
    setSavingRequest(true);
    setFormErrorRequest(null);
    try {
      await createOrgasmRequest(newRequest);
      setNewRequest({ user_id: '', reason: '' });
      setShowRequestModal(false);
      toast.success('Anfrage gesendet');
      fetchData();
    } catch (error) {
      console.error('Error creating request:', error);
      setFormErrorRequest(extractErrorMessage(error, 'Fehler beim Erstellen der Anfrage'));
    } finally {
      setSavingRequest(false);
    }
  };

  const resetAction = () => setNewAction({ user_id: '', action: 'allow', condition: '', duration: '', reason: '', count: '', timeframe: '', release_type: 'allowed' });

  const handleCreateAction = async (e) => {
    e.preventDefault();
    if (savingLog) return;
    setSavingLog(true);
    setFormErrorLog(null);
    try {
      const a = newAction;
      switch (a.action) {
        case 'allow':
          await controlAllow({ user_id: a.user_id, condition: a.condition || undefined, duration: a.duration ? parseInt(a.duration) : undefined });
          break;
        case 'deny':
          await controlDeny({ user_id: a.user_id, reason: a.reason || undefined, duration: a.duration ? parseInt(a.duration) : undefined });
          break;
        case 'edge':
          await controlEdge({ user_id: a.user_id, count: a.count ? parseInt(a.count) : undefined, timeframe: a.timeframe || undefined, condition: a.condition || undefined });
          break;
        case 'release':
          await controlRelease({ user_id: a.user_id, type: a.release_type, condition: a.condition || undefined });
          break;
        default:
          break;
      }
      resetAction();
      setShowLogModal(false);
      toast.success('Aktion ausgeführt');
      fetchData();
    } catch (error) {
      console.error('Error executing action:', error);
      setFormErrorLog(extractErrorMessage(error, 'Fehler beim Ausführen der Aktion'));
    } finally {
      setSavingLog(false);
    }
  };

  const handleRespond = async (id, decision) => {
    try {
      await respondOrgasmRequest(id, { decision });
      toast.success(decision === 'approved' ? 'Anfrage genehmigt' : 'Anfrage abgelehnt');
      fetchData();
    } catch (error) {
      console.error('Error responding to request:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Beantworten der Anfrage'));
    }
  };

  const handleDeleteLog = async (id) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    try {
      await deleteOrgasmLog(id);
      toast.success('Eintrag gelöscht');
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen'));
    }
  };

  const handleLogEdge = async () => {
    try {
      await controlLogEdge({ user_id: user.id });
      toast.success('Edge geloggt');
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Fehler beim Loggen'));
    }
  };

  const handleDeleteHistory = async (id) => {
    if (!window.confirm('Eintrag wirklich löschen? Status wird ggf. zurückgesetzt.')) return;
    try {
      await deleteControlHistory(id);
      toast.success('Eintrag gelöscht');
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen'));
    }
  };

  const handleDeleteRequest = async (id) => {
    if (!window.confirm('Anfrage wirklich löschen?')) return;
    try {
      await deleteOrgasmRequest(id);
      toast.success('Anfrage gelöscht');
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen'));
    }
  };

  const resolveUser = (userId) => membersMap[userId] || userId;

  const closeRequestModal = () => {
    if (savingRequest) return;
    setShowRequestModal(false);
    setFormErrorRequest(null);
  };

  const closeLogModal = () => {
    if (savingLog) return;
    setShowLogModal(false);
    setFormErrorLog(null);
  };

  // Extract the status string from any shape: string, { status: '...' }, { allowed: true/false }
  const resolveStatusVal = (s) => {
    if (!s) return 'allowed';
    if (typeof s === 'string') return s;
    if (s.status) return s.status;
    // Legacy fallback: { allowed: true/false }
    if ('allowed' in s) return s.allowed ? 'allowed' : 'denied';
    return 'allowed';
  };

  const getStatusColor = (s) => {
    const val = resolveStatusVal(s);
    const colors = {
      allow: '#43b581', allowed: '#43b581', approved: '#43b581',
      deny: '#f04747', denied: '#f04747',
      pending: '#faa61a',
      edge: '#7289da', edging: '#7289da', edged: '#7289da',
      release: '#ff69b4',
      ruined: '#e67e22',
      forced: '#9b59b6'
    };
    return colors[val] || '#faa61a';
  };

  const getStatusLabel = (s) => {
    const val = resolveStatusVal(s);
    const labels = {
      allow: 'Erlaubt', allowed: 'Erlaubt',
      approve: 'Genehmigt', approved: 'Genehmigt',
      deny: 'Verweigert', denied: 'Verweigert',
      pending: 'Ausstehend',
      edge: 'Edging', edging: 'Edging', edged: 'Edging',
      release: 'Release',
      ruined: 'Ruiniert',
      forced: 'Erzwungen'
    };
    return labels[val] || val || 'Erlaubt';
  };

  const getTypeColor = (type) => {
    const colors = {
      allow: '#43b581', allowed: '#43b581', approved: '#43b581',
      deny: '#f04747', denied: '#f04747',
      pending: '#faa61a',
      edge: '#7289da', edging: '#7289da', edged: '#7289da',
      release: '#ff69b4',
      ruined: '#e67e22',
      forced: '#9b59b6'
    };
    return colors[type] || '#faa61a';
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Orgasmuskontrolle</h1>
          <div className="header-actions">
            <button className="add-btn" onClick={() => setShowRequestModal(true)}>
              <FaPlus /> Neue Anfrage
            </button>
            <button className="add-btn secondary" onClick={() => setShowLogModal(true)}>
              <FaPlus /> Kontrolle
            </button>
          </div>
        </div>

        <div className="tab-bar">
          <button className={`tab-btn ${activeTab === 'status' ? 'active' : ''}`} onClick={() => setActiveTab('status')}>
            Status
          </button>
          <button className={`tab-btn ${activeTab === 'requests' ? 'active' : ''}`} onClick={() => setActiveTab('requests')}>
            Anfragen
          </button>
          <button className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
            Kontrollverlauf
          </button>
          <button className={`tab-btn ${activeTab === 'logs' ? 'active' : ''}`} onClick={() => setActiveTab('logs')}>
            Logs
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : activeTab === 'status' ? (
          <div className="status-display">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                backgroundColor: getStatusColor(status),
                margin: '0 auto 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 0 40px ${getStatusColor(status)}80`
              }}>
                <FaCircle style={{ fontSize: '40px', color: '#fff' }} />
              </div>
              <h2 style={{ color: getStatusColor(status), marginBottom: '10px' }}>
                {getStatusLabel(status)}
              </h2>

              {/* Edging details */}
              {status && resolveStatusVal(status) === 'edging' && (
                <div style={{ margin: '16px auto', maxWidth: '360px' }}>
                  {/* Requirement text from latest control entry */}
                  {status.latest_control && status.latest_control.condition && (
                    <p style={{ color: '#fff', fontSize: '1rem', marginBottom: '12px', fontWeight: 500 }}>
                      {status.latest_control.condition}
                    </p>
                  )}
                  {(status.edges_allowed > 0) && (
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ color: '#b9b9c8', fontSize: '0.9rem' }}>Fortschritt: </span>
                      <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
                        {status.edges_today || 0} / {status.edges_allowed}
                      </span>
                      <div style={{
                        height: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.1)',
                        marginTop: '6px', overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%', borderRadius: '4px',
                          background: getStatusColor(status),
                          width: `${Math.min(100, ((status.edges_today || 0) / (status.edges_allowed || 1)) * 100)}%`,
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </div>
                  )}
                  {(!status.edges_allowed || status.edges_allowed === 0) && status.edges_today > 0 && (
                    <p style={{ color: '#b9b9c8', fontSize: '0.9rem' }}>
                      Edges heute: <strong style={{ color: '#fff' }}>{status.edges_today}</strong>
                    </p>
                  )}
                  <button
                    className="btn btn-primary"
                    style={{ marginTop: '16px' }}
                    onClick={handleLogEdge}
                  >
                    🔥 Edge Loggen (+1)
                  </button>
                  {status.edges_allowed > 0 && status.edges_today >= status.edges_allowed && (
                    <p style={{ color: '#43b581', fontWeight: 700, marginTop: '12px', fontSize: '1rem' }}>
                      ✅ Alle Edges abgeschlossen!
                    </p>
                  )}
                </div>
              )}

              {/* Denied since */}
              {status && status.denied_since && resolveStatusVal(status) === 'denied' && (
                <p style={{ color: '#8e9297', marginTop: '8px' }}>
                  Verweigert seit: {new Date(status.denied_since).toLocaleString('de-DE')}
                </p>
              )}

              {/* Last orgasm */}
              {status && status.last_orgasm && (
                <p style={{ color: '#8e9297', marginTop: '4px', fontSize: '0.85rem' }}>
                  Letzter Release: {new Date(status.last_orgasm).toLocaleString('de-DE')}
                </p>
              )}

              {status && status.updated_at && (
                <p style={{ color: '#72767d', fontSize: '0.8rem', marginTop: '8px' }}>
                  Aktualisiert: {new Date(status.updated_at).toLocaleString('de-DE')}
                </p>
              )}
            </div>
          </div>
        ) : activeTab === 'history' ? (
          <div className="data-table-container">
            {history.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Benutzer</th>
                    <th>Aktion</th>
                    <th>Details</th>
                    <th>Von</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id}>
                      <td>{new Date(h.created_at).toLocaleString('de-DE')}</td>
                      <td>{resolveUser(h.user_id)}</td>
                      <td>
                        <span className="badge" style={{ backgroundColor: getTypeColor(h.action) }}>
                          {getStatusLabel(h.action)}
                        </span>
                      </td>
                      <td>{h.condition || '-'}{h.duration ? ` (${h.duration})` : ''}</td>
                      <td>{resolveUser(h.assigned_by)}</td>
                      <td className="col-actions">
                        <button className="icon-btn delete" onClick={() => handleDeleteHistory(h.id)} title="Löschen">
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Kein Kontrollverlauf vorhanden</div>
            )}
          </div>
        ) : activeTab === 'requests' ? (
          <div className="data-table-container">
            {requests.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Benutzer</th>
                    <th>Grund</th>
                    <th>Status</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(req => (
                    <tr key={req.id}>
                      <td>{new Date(req.created_at || req.timestamp).toLocaleString('de-DE')}</td>
                      <td>{resolveUser(req.user_id)}</td>
                      <td>{req.reason || '-'}</td>
                      <td>
                        <span className="badge" style={{ backgroundColor: getTypeColor(req.status) }}>
                          {getStatusLabel(req.status)}
                        </span>
                      </td>
                      <td className="col-actions">
                        {req.status !== 'approved' && (
                          <button className="icon-btn success" onClick={() => handleRespond(req.id, 'approved')} title="Genehmigen">
                            <FaCheck />
                          </button>
                        )}
                        {req.status !== 'denied' && (
                          <button className="icon-btn danger" onClick={() => handleRespond(req.id, 'denied')} title="Ablehnen">
                            <FaTimes />
                          </button>
                        )}
                        <button className="icon-btn delete" onClick={() => handleDeleteRequest(req.id)} title="Löschen">
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Keine Anfragen vorhanden</div>
            )}
          </div>
        ) : (
          <div className="data-table-container">
            {logs.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Benutzer</th>
                    <th>Typ</th>
                    <th>Notizen</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id}>
                      <td>{new Date(log.created_at || log.timestamp).toLocaleString('de-DE')}</td>
                      <td>{resolveUser(log.user_id)}</td>
                      <td>
                        <span className="badge" style={{ backgroundColor: getTypeColor(log.type) }}>
                          {getStatusLabel(log.type)}
                        </span>
                      </td>
                      <td>{log.notes || '-'}</td>
                      <td>
                        <button className="icon-btn delete" onClick={() => handleDeleteLog(log.id)} title="Löschen">
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Kein Verlauf vorhanden</div>
            )}
          </div>
        )}

        {showRequestModal && (
          <div className="modal-overlay" onClick={closeRequestModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neue Anfrage</h2>
              {formErrorRequest && <div className="shared-form-error-banner">{formErrorRequest}</div>}
              <form onSubmit={handleCreateRequest}>
                <div className="form-group">
                  <UserSelect
                    value={newRequest.user_id}
                    onChange={id => setNewRequest({ ...newRequest, user_id: id })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Grund</label>
                  <textarea
                    value={newRequest.reason}
                    onChange={e => setNewRequest({ ...newRequest, reason: e.target.value })}
                    placeholder="Grund für die Anfrage..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeRequestModal} disabled={savingRequest}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingRequest}>
                    {savingRequest ? 'Speichert\u2026' : 'Anfrage senden'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showLogModal && (
          <div className="modal-overlay" onClick={closeLogModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Kontrolle Ausführen</h2>
              {formErrorLog && <div className="shared-form-error-banner">{formErrorLog}</div>}
              <form onSubmit={handleCreateAction}>
                <div className="form-group">
                  <UserSelect
                    value={newAction.user_id}
                    onChange={id => setNewAction({ ...newAction, user_id: id })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Aktion</label>
                  <select
                    value={newAction.action}
                    onChange={e => setNewAction({ ...newAction, action: e.target.value })}
                  >
                    <option value="allow">Erlauben</option>
                    <option value="deny">Verweigern</option>
                    <option value="edge">Edging</option>
                    <option value="release">Release</option>
                  </select>
                </div>

                {/* Edging: count + timeframe */}
                {newAction.action === 'edge' && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Anzahl Edges</label>
                        <input
                          type="number" min="1"
                          value={newAction.count}
                          onChange={e => setNewAction({ ...newAction, count: e.target.value })}
                          placeholder="z.B. 20"
                        />
                      </div>
                      <div className="form-group">
                        <label>Zeitraum</label>
                        <input
                          type="text"
                          value={newAction.timeframe}
                          onChange={e => setNewAction({ ...newAction, timeframe: e.target.value })}
                          placeholder="z.B. 20 Minuten"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Deny: reason + duration */}
                {newAction.action === 'deny' && (
                  <>
                    <div className="form-group">
                      <label>Grund</label>
                      <textarea
                        value={newAction.reason}
                        onChange={e => setNewAction({ ...newAction, reason: e.target.value })}
                        placeholder="Grund für die Verweigerung..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Dauer (Stunden)</label>
                      <input
                        type="number" min="1"
                        value={newAction.duration}
                        onChange={e => setNewAction({ ...newAction, duration: e.target.value })}
                        placeholder="Optional"
                      />
                    </div>
                  </>
                )}

                {/* Allow: condition + duration */}
                {newAction.action === 'allow' && (
                  <>
                    <div className="form-group">
                      <label>Bedingung</label>
                      <textarea
                        value={newAction.condition}
                        onChange={e => setNewAction({ ...newAction, condition: e.target.value })}
                        placeholder="Bedingungen (optional)..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Dauer (Stunden)</label>
                      <input
                        type="number" min="1"
                        value={newAction.duration}
                        onChange={e => setNewAction({ ...newAction, duration: e.target.value })}
                        placeholder="Optional"
                      />
                    </div>
                  </>
                )}

                {/* Release: type + condition */}
                {newAction.action === 'release' && (
                  <>
                    <div className="form-group">
                      <label>Typ</label>
                      <select
                        value={newAction.release_type}
                        onChange={e => setNewAction({ ...newAction, release_type: e.target.value })}
                      >
                        <option value="allowed">Erlaubt</option>
                        <option value="ruined">Ruiniert</option>
                        <option value="forced">Erzwungen</option>
                      </select>
                    </div>
                  </>
                )}

                {/* Condition for edge + release */}
                {(newAction.action === 'edge' || newAction.action === 'release') && (
                  <div className="form-group">
                    <label>Anweisungen / Bedingungen</label>
                    <textarea
                      value={newAction.condition}
                      onChange={e => setNewAction({ ...newAction, condition: e.target.value })}
                      placeholder="Optional..."
                    />
                  </div>
                )}

                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeLogModal} disabled={savingLog}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingLog}>
                    {savingLog ? 'Speichert\u2026' : 'Ausführen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default OrgasmControl;
